/**
 * Locks & Keys — structurele metadata van de globale-cyclussenvisualisatie
 * (spec `docs/superpowers/specs/2026-08-27-hd-locks-keys-design.md`).
 *
 * De 8 Locks zijn de eeuwig vaststaande poorten van het G-centrum, samen de
 * twee incarnatiekruisen Cross of the Sphinx (1, 2, 7, 13) en Cross of the
 * Vessel of Love (10, 15, 25, 46). De Keys zijn de 8 armen van de
 * precessie-ster die per tijdperk van 411 jaar één poort retrograde opschuift.
 *
 * Geometrie: zowel de locks als de keys liggen exact 8 posities uit elkaar in
 * de wielvolgorde ({@link MANDALA_GATE_SEQUENCE_CCW}); de key van een lock
 * staat in de huidige periode exact 4 wielposities vóór zijn lock en in de
 * volgende periode 5 (één poort retrograde opgeschoven). Daardoor wisselt een
 * key nooit van lock. De spec-test verifieert deze invariant tegen de
 * mandala-poortvolgorde.
 *
 * Let op bij de bron (`docs/human-design/books/de-kosmische-wijzers-locks-keys-v2.md`):
 * de tabel dáár wisselt de keys van locks 1↔2 en 10↔15 om; de mapping hier
 * volgt de geometrisch geverifieerde paring, consistent met de
 * "Wheel of 2027"- en "Master cycles"-referenties.
 *
 * Vertaalbare content (namen, beschrijvingen, analistnotities) leeft in de
 * Transloco-scope `human-design/locks-keys` (nl/en), ontsloten via
 * `HdGateInfoService.locksKeysTextIn()`.
 */

import moment, { Moment } from 'moment';
import { MANDALA_GATE_SEQUENCE_CCW } from '../mandala.constants';
import { HD_GLOBAL_SHIFT_DATE, HD_GLOBAL_SHIFT_MS, HD_GLOBAL_SHIFT_UTC } from '../../hd-global-shift';
import { HD_GLOBAL_CYCLES_ROWS } from './hd-global-cycles-table.data';

/** Ster-groep van een lock: welk incarnatiekruis van het G-centrum. */
export type HdLocksKeysStarGroup = 'sphinx' | 'vessel';

/**
 * Precessie-periode: elk van de 49 tijdperken (411 jaar) uit de Global
 * Cycles-tabel. De periodes met eigen content dragen een benoemde id
 * (`planning`, `sleepingPhoenix`, `contagion`, `explanation`), de andere hun
 * tabelrij-id (bv. `block-2-key-3`). Bewaard in de settings, dus id's nooit
 * hernoemen.
 */
export type HdLocksKeysPeriodId = string;

export const HD_LOCKS_KEYS_PLANNING_PERIOD_ID: HdLocksKeysPeriodId = 'planning';
export const HD_LOCKS_KEYS_PHOENIX_PERIOD_ID: HdLocksKeysPeriodId = 'sleepingPhoenix';
export const HD_LOCKS_KEYS_CONTAGION_PERIOD_ID: HdLocksKeysPeriodId = 'contagion';
export const HD_LOCKS_KEYS_EXPLANATION_PERIOD_ID: HdLocksKeysPeriodId = 'explanation';


/** Weergave-scope: neutrale locks & keys, of toegepast op de geladen chart. */
export type HdLocksKeysScope = 'general' | 'chart';

/**
 * Locks & keys op de bodygraph buiten de modus (Geavanceerd-paneel): niet
 * tonen, alle locks + de keys van de periode, of enkel de poorten die de chart
 * activeert. De volgorde is ook de klikvolgorde van de drie-standen-toggle.
 */
export type HdLocksKeysChartOverlayMode = 'off' | 'all' | 'chart';

export const HD_LOCKS_KEYS_CHART_OVERLAY_MODES: readonly HdLocksKeysChartOverlayMode[] = ['off', 'all', 'chart'];

/**
 * Sortering van de L&K-zoekresultaten: eerst op aantal relaties en dan
 * alfabetisch, of enkel alfabetisch.
 */
export type HdLocksKeysSearchSort = 'relations' | 'name';

/**
 * Filter op de L&K-zoekresultaten: alle relaties, of enkel die waarbij de
 * ander zijn key/lock met zon, aarde, noord- of zuidknoop activeert (een key of
 * lock in zijn incarnatie- of levenskruis).
 */
export type HdLocksKeysSearchFilter = 'all' | 'cross';

export interface HdLocksKeysPeriodMeta {
  readonly id: HdLocksKeysPeriodId;
  /** Jaartallen met teken: v.Chr. negatief (geen jaar nul in de bron). */
  readonly yearFrom: number;
  readonly yearTo: number;
  /** Jaarlabel zoals in de Global Cycles-tabel, bv. `1615-2026 AD`. */
  readonly yearLabel: string;
  /** Poort.lijn waar de precessie-equinox de periode binnenvalt (enkel waar gedocumenteerd). */
  readonly startsAt?: string;
  /**
   * Exact UTC-startmoment van het tijdperk, het moment waarop ook zijn cycle
   * chart gebaseerd is (enkel waar de bron het geeft; anders telt het jaartal).
   */
  readonly startsAtUtc?: string;
  /** Wielposities dat elke key retrograde van zijn lock staat; 0 = lock-fase (slot en sleutel vallen samen). */
  readonly keyShift: number;
  readonly vesselCross: string;
  readonly sphinxCross: string;
  /** Poort van het lentepunt (de precessie-wijzer): kolom 1 links in de Global Cycles-tabel. */
  readonly equinoxGate: number;
  /**
   * Lock-kruis dat het vierkant van het lentepunt in deze periode ontsluit. Wisselt
   * bij elke lock-fase: de wijzer landt dan op een lock van het andere kruis.
   */
  readonly equinoxGroup: HdLocksKeysStarGroup;
}

export interface HdLocksKeysLockMeta {
  /** G-centrum poort van het slot. */
  readonly gate: number;
  readonly group: HdLocksKeysStarGroup;
}

const DOCUMENTED_STARTS_AT: Readonly<Record<string, string>> = {
  planning: '37.1',
  sleepingPhoenix: '55.6',
};

/**
 * Enkel de 2027-shift heeft een overgeleverd tijdstip ("the Ides of February
 * 2027", 22:10 GMT); voor 1615 en de andere tijdperken geeft de bron enkel het
 * jaartal (`docs/human-design/locks-keys-cyclus-momenten.md`).
 */
const DOCUMENTED_STARTS_AT_UTC: Readonly<Record<string, string>> = {
  sleepingPhoenix: HD_GLOBAL_SHIFT_UTC,
};

/** `1615-2026 AD` / `16513-16103 BC` / `33 BC-378 AD` naar jaartallen met teken. */
function parseHdGlobalCyclesYears(label: string): { yearFrom: number; yearTo: number } {
  const match = /^(\d+)(?:\s*(BC|AD))?-(\d+)\s*(BC|AD)$/.exec(label.trim());
  if (match == null) {
    return { yearFrom: 0, yearTo: 0 };
  }
  const toEra = match[4];
  const fromEra = match[2] ?? toEra;
  const signed = (value: string, era: string) => (era === 'BC' ? -Number(value) : Number(value));
  return { yearFrom: signed(match[1], fromEra), yearTo: signed(match[3], toEra) };
}

/**
 * Alle 49 periodes in chronologische volgorde, mechanisch afgeleid uit de
 * Global Cycles-tabel (zelfde bron als de tabel op de infopagina).
 */
export const HD_LOCKS_KEYS_PERIODS: readonly HdLocksKeysPeriodMeta[] = HD_GLOBAL_CYCLES_ROWS.map(row => ({
  id: row.periodId,
  ...parseHdGlobalCyclesYears(row.yearLabel),
  yearLabel: row.yearLabel,
  ...(DOCUMENTED_STARTS_AT[row.periodId] != null ? { startsAt: DOCUMENTED_STARTS_AT[row.periodId] } : {}),
  ...(DOCUMENTED_STARTS_AT_UTC[row.periodId] != null ? { startsAtUtc: DOCUMENTED_STARTS_AT_UTC[row.periodId] } : {}),
  keyShift: row.keyShift,
  vesselCross: row.vesselCross,
  sphinxCross: row.sphinxCross,
  equinoxGate: row.leftGates[0],
  equinoxGroup: row.leftGroup,
}));

export const HD_LOCKS_KEYS_DEFAULT_PERIOD_ID: HdLocksKeysPeriodId = HD_LOCKS_KEYS_PLANNING_PERIOD_ID;

/** Het standaardpaar van de tweeluik-toggle: de periode van nu en die na de 2027-shift. */
export const HD_LOCKS_KEYS_DEFAULT_PERIOD_PAIR: readonly [HdLocksKeysPeriodId, HdLocksKeysPeriodId] = [
  HD_LOCKS_KEYS_PLANNING_PERIOD_ID,
  HD_LOCKS_KEYS_PHOENIX_PERIOD_ID,
];

/** Bestaat deze periode-id (bv. bij het herstellen van bewaarde settings)? */
export function isHdLocksKeysPeriodId(id: unknown): id is HdLocksKeysPeriodId {
  return typeof id === 'string' && HD_LOCKS_KEYS_PERIODS.some(period => period.id === id);
}

/** Chronologische index van een periode (0 = 16513 v.Chr.); -1 als onbekend. */
export function hdLocksKeysPeriodIndex(id: HdLocksKeysPeriodId): number {
  return HD_LOCKS_KEYS_PERIODS.findIndex(period => period.id === id);
}

/**
 * De gedocumenteerde precessie-shift van de Cross of Planning naar de
 * Sleeping Phoenix (37 naar 55.6), centraal in `hd-global-shift.ts`.
 */
export const HD_LOCKS_KEYS_SHIFT_DATE = HD_GLOBAL_SHIFT_DATE;

/**
 * De periode die op een gegeven moment de "actieve" achtergrond is: vóór de
 * 2027-shift (15 februari 2027, 22:10 GMT) is dat Planning, erna de Sleeping
 * Phoenix. Voedt de standaardkeuze van de relatie-sectie (composite) en de
 * poort-info: die tonen default de periode van nu, togglebaar naar de andere.
 */
export function hdLocksKeysPeriodForDate(now: Moment = moment()): HdLocksKeysPeriodId {
  return now.valueOf() >= HD_GLOBAL_SHIFT_MS
    ? HD_LOCKS_KEYS_PHOENIX_PERIOD_ID
    : HD_LOCKS_KEYS_PLANNING_PERIOD_ID;
}

/**
 * De 8 locks in presentatievolgorde: eerst de Vessel of Love (identiteit en
 * liefde), dan de Sphinx (richting) — de volgorde van het bronmateriaal.
 */
export const HD_LOCKS_KEYS_LOCKS: readonly HdLocksKeysLockMeta[] = [
  { gate: 25, group: 'vessel' },
  { gate: 46, group: 'vessel' },
  { gate: 10, group: 'vessel' },
  { gate: 15, group: 'vessel' },
  { gate: 13, group: 'sphinx' },
  { gate: 7, group: 'sphinx' },
  { gate: 2, group: 'sphinx' },
  { gate: 1, group: 'sphinx' },
];

const GATE_SEQUENCE_INDEX = new Map<number, number>(MANDALA_GATE_SEQUENCE_CCW.map((gate, index) => [gate, index]));

/**
 * De key-poort die een slot in een periode ontsluit: `keyShift` posities
 * retrograde van de lock in de wielvolgorde (Planning 4, Sleeping Phoenix 5,
 * Contagion 6, Explanation 7; in een lock-fase 0, de lock zelf). Geverifieerd tegen de
 * paringen uit de klas en het Wheel of 2027 in hd-locks-keys.spec.ts.
 */
export function hdLocksKeysKeyGate(lockGate: number, periodId: HdLocksKeysPeriodId): number {
  const shift = getHdLocksKeysPeriodMeta(periodId).keyShift;
  const index = GATE_SEQUENCE_INDEX.get(lockGate) ?? 0;
  const length = MANDALA_GATE_SEQUENCE_CCW.length;
  return MANDALA_GATE_SEQUENCE_CCW[(index - shift + length) % length];
}

/**
 * Sectie-keys van de intro/mechanica-uitleg in de i18n-scope (`intro.<key>.*`).
 * Per sectie: `title` + `paragraphs`, optioneel `items` (genummerde punten) en
 * `note` (analistnotitie-quote). Volgorde = weergavevolgorde op de infopagina.
 */
export const HD_LOCKS_KEYS_INTRO_SECTION_KEYS: readonly string[] = [
  'clock',
  'locks',
  'keys',
  'quarterPairs',
  'agents',
  'indicators',
  'lineEra',
  'planningName',
  'channelShift',
  'projectorKey',
  'tableOrder',
  'spiral',
  'greatRound',
  'sevenBySeven',
  'endOfRound',
  'threeQuarters',
  'lockPhases',
  'pluto',
  'plutoDoors',
  'plutoRave',
];

/**
 * Sectie-keys van het uitgelichte vervolgpaneel "Mens na 2027"
 * (`postShift.<key>.*`): blijft de chart geldig, de breuk van 19/49, de
 * kanalen die vervlakken, de Solar Plexus van mensen na de shift, de tijdlijn
 * van samenleven en Strategie en Autoriteit, met een bronnenoverzicht als slot.
 * Zelfde sectiemodel als de intro (title/paragraphs/items/closing/note), plus
 * per sectie een `sources`-lijst waarnaar `[n]` in de tekst verwijst.
 */
export const HD_LOCKS_KEYS_POST_SHIFT_SECTION_KEYS: readonly string[] = [
  'chartValid',
  // Signaal, achtergrond en ontvanger: waarom de keynotes niet vervallen. Enige plek
  // voor deze synthese; Steven werkt ze hier bij naarmate er meer duidelijkheid komt.
  'keynoteLayers',
  'synthesisBreak',
  'flattening',
  'solarPlexus',
  'timeline',
  'authority',
  // Ra's "epoch for the perfection of the human" en de rechtse mensen van na de mutatie.
  'perfection',
  // Bronnenoverzicht: de werken achter de `[n]`-verwijzingen per sectie en hoe de
  // paginanummers tot stand kwamen. Gevoelig materiaal: elke uitspraak hoort een bron te hebben.
  'bibliography',
];

/**
 * Sectie-keys van het vervolgpaneel "Het einde van de Ronde" (`roundEnd.<key>.*`):
 * Ra's apocalyps uit Rave Cosmology V les 5 (onthulling, geen oordeel, de inslag,
 * de dood van een mens naast die van de planeet, de culling van de yang/yang,
 * Neptunus, de herbundeling volgens fractalen, het laatste slot). Gevoelig
 * materiaal, dus zoals "Mens na 2027" met `sources` per sectie en een
 * bronnenoverzicht als slot. De volgende les (de Nacht van Brahma) hoort hier bij.
 */
export const HD_LOCKS_KEYS_ROUND_END_SECTION_KEYS: readonly string[] = [
  'unveiling',
  'noJudgment',
  'howItEnds',
  'deathParallel',
  'culling',
  'neptune',
  'fractalBundle',
  'lastLock',
  'bibliography',
];

// De biologische vervolgcontent (`bio.<key>.*`) staat in drie vervolgpanelen;
// één paneel met alle twintig-plus secties werd te lang om te lezen. Zelfde
// sectiemodel als de intro; geopend via de doorklikkaarten onderaan het
// intro-paneel. Volgorde = weergavevolgorde per paneel.

/** Vervolgpaneel "Biologische shift": histidine, de vier fasen, 3/60, vruchtbaarheid, vlees. */
export const HD_LOCKS_KEYS_BIO_SHIFT_SECTION_KEYS: readonly string[] = [
  'histidine',
  'solarPlexusStages',
  'mutationChannel',
  'fertility',
  'meatBreak',
  'summary',
];

/** Vervolgpaneel "Rave en Penta": de eerste Rave-kinderen, Penta-binding, voortplanting, brugkinderen. */
export const HD_LOCKS_KEYS_BIO_RAVE_SECTION_KEYS: readonly string[] = [
  'raveParents',
  'raveBaby',
  'penta',
  'pentaBinding',
  'raveKeynotes',
  // 63 en 4 bij de Rave (RC IV les 6): de logica zonder twijfel, overgedragen aan de Penta.
  'raveLogic',
  'raveReproduction',
  'raveSupplyLine',
  'raveCoexistence',
  'raveFuture',
  'raveIndifference',
  'raveIrony',
  'lawsCross',
  'lawsTranslation',
  'lawsRecognition',
];

/** Vervolgpaneel "Nodale omgeving": de Nodes, de nodale splitsing en de omgeving na 2027. */
export const HD_LOCKS_KEYS_BIO_NODES_SECTION_KEYS: readonly string[] = [
  'nodes',
  'nodalSplit',
  'nodesEnvironment',
  'nodesSummary',
];

/** Alle secties onder `bio.*`, over de drie vervolgpanelen heen (drift-guard). */
export const HD_LOCKS_KEYS_BIO_SECTION_KEYS: readonly string[] = [
  ...HD_LOCKS_KEYS_BIO_SHIFT_SECTION_KEYS,
  ...HD_LOCKS_KEYS_BIO_RAVE_SECTION_KEYS,
  ...HD_LOCKS_KEYS_BIO_NODES_SECTION_KEYS,
];

const LOCK_BY_GATE = new Map<number, HdLocksKeysLockMeta>(
  HD_LOCKS_KEYS_LOCKS.map(lock => [lock.gate, lock]),
);

export function getHdLocksKeysLockMeta(gate: number): HdLocksKeysLockMeta | undefined {
  return LOCK_BY_GATE.get(gate);
}

export function getHdLocksKeysPeriodMeta(id: HdLocksKeysPeriodId): HdLocksKeysPeriodMeta {
  const period = HD_LOCKS_KEYS_PERIODS.find(entry => entry.id === id);
  if (period == null) {
    throw new Error(`Onbekende Locks & Keys periode: ${id}`);
  }
  return period;
}

/**
 * Rol van een poort in Locks & Keys: een vaste lock van het G-center, of de key
 * van een lock in één van de periodes. De keys van de twee periodes zijn
 * disjunct en een lock is nooit een key, dus elke poort heeft hooguit één rol.
 */
export type HdLocksKeysGateRole =
  | { readonly kind: 'lock'; readonly lock: HdLocksKeysLockMeta }
  | { readonly kind: 'key'; readonly lock: HdLocksKeysLockMeta; readonly periodId: HdLocksKeysPeriodId };

/**
 * De twee periodes rond de 2027-shift: de poort-info toont enkel daar een
 * slot- of sleutelrol (elke poort is in een of ander tijdperk wel ergens een key).
 */
export const HD_LOCKS_KEYS_SHIFT_PERIOD_IDS: readonly HdLocksKeysPeriodId[] = [
  HD_LOCKS_KEYS_PLANNING_PERIOD_ID,
  HD_LOCKS_KEYS_PHOENIX_PERIOD_ID,
];

export function getHdLocksKeysGateRole(gate: number): HdLocksKeysGateRole | null {
  const lock = LOCK_BY_GATE.get(gate);
  if (lock) {
    return { kind: 'lock', lock };
  }
  for (const candidate of HD_LOCKS_KEYS_LOCKS) {
    for (const periodId of HD_LOCKS_KEYS_SHIFT_PERIOD_IDS) {
      if (hdLocksKeysKeyGate(candidate.gate, periodId) === gate) {
        return { kind: 'key', lock: candidate, periodId };
      }
    }
  }
  return null;
}

/** Lookup key-poort → lock voor een periode (voor activatie-checks per chart). */
export function buildHdLocksKeysKeyGateMap(
  periodId: HdLocksKeysPeriodId,
): ReadonlyMap<number, HdLocksKeysLockMeta> {
  return new Map(HD_LOCKS_KEYS_LOCKS.map(lock => [hdLocksKeysKeyGate(lock.gate, periodId), lock]));
}
